import { useLingui } from '@lingui/react/macro';
import { useRouterState } from '@tanstack/react-router';
import { ArrowBigUpDash, ArrowRight, Check, Eye, EyeOff, LockKeyhole, type LucideIcon } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useController, type Control, type FieldError as FormError, type UseFormRegisterReturn } from 'react-hook-form';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { CodeInput, type CodeInputStatus } from '@/shared/ui/code-input';
import { FormField } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';

import { passwordChecks, type PasswordCheck } from './auth-schemas';

/** Altura dos campos do login: maior que a da tabela no desktop, a da plataforma no celular. */
const AUTH_CONTROL = 'md:h-11';

/** Título e texto de apoio de cada tela de login, com o ícone do passo e o passo da jornada. */
export function AuthHeading({ title, icon: Icon, eyebrow, children }: { title: ReactNode; icon?: LucideIcon; eyebrow?: ReactNode; children?: ReactNode }) {
  return (
    <header className="flex flex-col gap-5">
      {Icon ? (
        <span aria-hidden className="inline-flex size-12 items-center justify-center rounded-2xl bg-accent-wash text-accent-text ring-1 ring-accent-line ring-inset">
          <Icon className="size-[22px]" strokeWidth={1.75} />
        </span>
      ) : null}
      <div className="flex flex-col gap-2">
        {eyebrow ? <p className="text-xs font-semibold tracking-[0.08em] text-accent-text uppercase">{eyebrow}</p> : null}
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-[28px] md:leading-9">{title}</h1>
        {children ? <div className="text-sm text-muted-foreground md:text-[15px] md:leading-6">{children}</div> : null}
      </div>
    </header>
  );
}

/** O `submitError` do legado: aviso com ícone acima do formulário, que treme ao chegar. */
export function AuthError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return (
    <Alert key={message} tone="err" className="uyzy-shake">
      {message}
    </Alert>
  );
}

interface AuthFieldProps {
  label: ReactNode;
  registration: UseFormRegisterReturn;
  error: FormError | undefined;
  type?: 'text' | 'email' | 'password';
  autoComplete?: string;
  inputMode?: 'numeric' | 'email' | 'text';
  maxLength?: number;
  hint?: ReactNode;
  /** Ícone à esquerda, que acende com o foco. */
  icon?: LucideIcon;
  /** Código digitado (fonte mono, espaçada). */
  code?: boolean;
}

function LeadingIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <Icon
      aria-hidden
      className="pointer-events-none absolute top-1/2 left-3 size-[18px] -translate-y-1/2 text-muted-foreground transition-colors duration-(--lume-dur-fast) peer-focus-visible:text-accent-text"
      strokeWidth={1.75}
    />
  );
}

/** Campo de texto ligado ao react-hook-form. */
export function AuthField({ label, registration, error, type = 'text', autoComplete, inputMode, maxLength, hint, icon, code = false }: AuthFieldProps) {
  return (
    <FormField name={registration.name} label={label} required error={error?.message} hint={hint}>
      {(control) => (
        <div className="relative">
          <Input
            {...control}
            {...registration}
            type={type}
            spellCheck={false}
            {...(autoComplete ? { autoComplete } : {})}
            {...(inputMode ? { inputMode } : {})}
            {...(maxLength ? { maxLength } : {})}
            className={cn('peer', AUTH_CONTROL, icon && 'pl-10', code && 'font-mono tracking-[0.2em]')}
          />
          {icon ? <LeadingIcon icon={icon} /> : null}
        </div>
      )}
    </FormField>
  );
}

/** Senha com mostrar/ocultar e o aviso de Caps Lock ligado. */
export function AuthPasswordField({
  label,
  registration,
  error,
  autoComplete,
  hint,
}: Pick<AuthFieldProps, 'label' | 'registration' | 'error' | 'autoComplete' | 'hint'>) {
  const { t } = useLingui();
  const [visible, setVisible] = useState(false);
  const [caps, setCaps] = useState(false);
  const readCaps = (event: KeyboardEvent<HTMLInputElement>) => setCaps(event.getModifierState('CapsLock'));
  const capsNotice = caps ? (
    <span role="status" className="inline-flex items-center gap-1 font-medium text-warn">
      <ArrowBigUpDash aria-hidden className="size-3.5" />
      {t`Caps Lock ativado`}
    </span>
  ) : null;
  const notes = capsNotice || hint ? (
    <>
      {capsNotice}
      {capsNotice && hint ? <span className="block h-1" /> : null}
      {hint}
    </>
  ) : undefined;
  return (
    <FormField name={registration.name} label={label} required error={error?.message} hint={notes}>
      {(control) => (
        <div className="relative">
          <Input
            {...control}
            {...registration}
            type={visible ? 'text' : 'password'}
            spellCheck={false}
            autoCapitalize="none"
            {...(autoComplete ? { autoComplete } : {})}
            onKeyDown={readCaps}
            onKeyUp={readCaps}
            onBlur={(event) => {
              setCaps(false);
              void registration.onBlur(event);
            }}
            className={cn('peer pr-11 pl-10', AUTH_CONTROL)}
          />
          <LeadingIcon icon={LockKeyhole} />
          <button
            type="button"
            aria-label={visible ? t`Ocultar senha` : t`Mostrar senha`}
            aria-controls={control.id}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setVisible((v) => !v)}
            className="absolute top-1/2 right-1.5 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors duration-(--lume-dur-fast) hover:bg-muted hover:text-foreground"
          >
            {visible ? <EyeOff aria-hidden className="size-[18px]" strokeWidth={1.75} /> : <Eye aria-hidden className="size-[18px]" strokeWidth={1.75} />}
          </button>
        </div>
      )}
    </FormField>
  );
}

/** As regras da senha nova, marcadas enquanto a pessoa digita (a validação continua no zod). */
export function PasswordChecklist({ value }: { value: string }) {
  const { t } = useLingui();
  const checks = passwordChecks(value);
  const labels: Readonly<Record<PasswordCheck, string>> = {
    length: t`8 caracteres ou mais`,
    upper: t`Uma letra maiúscula`,
    lower: t`Uma letra minúscula`,
    number: t`Um número`,
    special: t`Um caractere especial (!@#$%^&*)`,
  };
  return (
    <ul aria-label={t`Requisitos da senha`} className="grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-xl border border-border bg-card px-3.5 py-3 text-xs sm:grid-cols-2">
      {(Object.keys(labels) as PasswordCheck[]).map((key) => (
        <li key={key} data-ok={checks[key]} className="flex items-center gap-2 text-muted-foreground transition-colors duration-(--lume-dur-fast) data-[ok=true]:text-ok">
          <span
            aria-hidden
            className={cn(
              'inline-flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-(--lume-dur-fast)',
              checks[key] ? 'border-transparent bg-ok-wash' : 'border-input',
            )}
          >
            {checks[key] ? <Check className="size-3" strokeWidth={3} /> : null}
          </span>
          <span>
            {labels[key]}
            <span className="sr-only">{checks[key] ? t`: atendido` : t`: pendente`}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Código de 6 dígitos nos slots do `CodeInput` (CodeSlots do React Bits, na identidade do Lume). Por baixo é
 * um campo só (`one-time-code`): colar, preencher pelo iOS e o leitor de tela funcionam como num campo
 * comum. `status`: `error` drena e limpa os slots sozinho; `success` lava com o accent e mostra o check.
 */
export function AuthCodeField({
  control,
  label,
  error,
  onComplete,
  status = 'idle',
  length = 6,
}: {
  control: Control<{ code: string }>;
  label: string;
  error: FormError | undefined;
  /** Chamado quando o último dígito entra (digitado, colado ou preenchido). */
  onComplete?: (code: string) => void;
  status?: CodeInputStatus;
  length?: number;
}) {
  const { field } = useController({ control, name: 'code' });
  return (
    <FormField name={field.name} label={label} required error={error?.message}>
      {(ids) => (
        <CodeInput
          id={ids.id}
          name={field.name}
          required
          aria-labelledby={ids['aria-labelledby']}
          {...(ids['aria-describedby'] ? { 'aria-describedby': ids['aria-describedby'] } : {})}
          ref={field.ref}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          status={status}
          invalid={Boolean(ids['aria-invalid'])}
          length={length}
          // Tela de um campo só (MFA): o código já começa focado, como antes.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          slotColor="var(--lume-bg-raise)"
          {...(onComplete ? { onComplete } : {})}
        />
      )}
    </FormField>
  );
}

export function AuthSubmit({ pending, children, arrow = true }: { pending: boolean; children: ReactNode; arrow?: boolean }) {
  // Depois do envio, a próxima tela (a home, com a casca e os dados) ainda carrega: o botão segue ocupado até ela chegar.
  const routing = useRouterState({ select: (state) => state.status === 'pending' });
  const busy = pending || routing;
  return (
    <Button type="submit" block size="lg" loading={busy} className={cn('group/submit shadow-1', AUTH_CONTROL)}>
      {children}
      {arrow && !busy ? <ArrowRight aria-hidden className="size-4 transition-transform duration-(--lume-dur-fast) group-hover/submit:translate-x-0.5" /> : null}
    </Button>
  );
}

/** Nota de rodapé de um passo (perdeu o dispositivo, para onde vai o código). */
export function AuthNote({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <p className="flex items-start gap-2.5 rounded-xl bg-card px-3.5 py-3 text-xs leading-5 text-muted-foreground">
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
      <span>{children}</span>
    </p>
  );
}

/** Classe dos links de navegação entre os passos (voltar, esqueci a senha). */
export const authLinkClass =
  'inline-flex items-center gap-1.5 rounded-sm text-sm font-medium text-accent-text no-underline underline-offset-4 hover:underline';
